import { PriorityQueue } from "common/data/priorityQueue";
import { ensureDefined } from "common/utils/assertOfType";
import { WindingDir } from "../core";
import type { CurveCurveIntersection } from "../intersectLine2Line2";
import { CurveCurvePointIntersection, CurveCurveRangeIntersection, intersectLine2Line2 } from "../intersectLine2Line2";
import type { Line2Segment } from "../line2";
import { line2PerpDistance, line2Side, line2Vec } from "../line2";
import {
  polyLine2Copy,
  polyLine2Edge,
  polyLine2EdgeCount,
  polyLine2EdgeLength,
  polyLine2EdgeNormal,
  polyLine2IsClosed,
  polyLine2Move,
  polyLine2Reverse,
  polyLine2SetCCW,
  polyLine2SetCW,
  polyLine2SetWindingDir,
  polyLine2Simplify,
  polyLine2Vertex,
  polyLine2WindingDir,
} from "../polyLine2";
import { polyLine2New, type PolyLine2 } from "../polyLine2Types";
import type { Vec2 } from "../vec2";
import { vec2Dist, vec2IsBetweenUsingLengthBasedCalc, vec2Scale } from "../vec2";
import { DEBUG_CONFIG, DEFAULT_CONFIG } from "./skeletonConstants";
import type { SkeletonConfig, EdgeSpeeds, SkeletonLine, SkeletonEvent, StraightSkeletonResult } from "./skeletonTypes";
import { SkeletonError } from "./skeletonTypes";

interface StraightSkeletonCtx {
  readonly offset: number;
  readonly speeds: EdgeSpeeds;
  readonly reversed: boolean;
  readonly errors: SkeletonError[];
}

/**
 * this implementation of the straight skeleton algorithm uses the "time" abstraction to
 * determine the order of events. time is a scalar value that represents the distance
 * from the generating edge of the polyline
 */
const TIME_EPSILON = 0.001;
/**
 * used to determine if two events are close enough in space to be considered the same
 */
const CLUSTER_EPSILON = 0.01;

const NO_OP_CTX: StraightSkeletonCtx = {
  speeds: 0,
  offset: 0,
  reversed: false,
  errors: [],
};

let lastEventId = 0;
function skeletonEventNew(from?: Partial<SkeletonEvent>): SkeletonEvent {
  const { id, time = 0, position = [0, 0], incoming = [], valid = true } = from ?? {};
  return {
    id: id ?? ++lastEventId, // id increases monotonically unless specified
    time,
    position,
    incoming,
    valid,
  };
}

function validEvent(e: SkeletonEvent | null): e is SkeletonEvent {
  if (e === null) {
    return false;
  }
  if (!e.valid) {
    return false;
  }
  // check to ensure that this event is also still the end event for all incoming lines
  for (const incoming of e.incoming) {
    const incomingEnd = incoming.queue.peek();
    if (incomingEnd === null) {
      // the incoming line can have no end event for start events; ignore them
      continue;
    }
    if (incomingEnd !== e) {
      return false;
    }
  }
  return true;
}

let lastLineId = 0;
function skeletonLineNew(from?: Partial<SkeletonLine>): SkeletonLine {
  const {
    id = ++lastLineId, // id inceases monotonically unless specified
    start = skeletonEventNew(),
    dir = [0, 0],
    neighbors = [],
    edges = [],
    infiniteSpeed = false,
    queue = new PriorityQueue<SkeletonEvent>(),
  } = from ?? {};
  return {
    id,
    start,
    dir,
    neighbors,
    edges,
    infiniteSpeed,
    queue,
  };
}

function popUntilValid(line: SkeletonLine): SkeletonEvent | null {
  const event = line.queue.peek();
  if (event === null) {
    return null;
  }
  if (!event.valid) {
    line.queue.pop();
    return popUntilValid(line);
  }
  return event;
}

function inRange(test: number, r0: number, r1: number, epsilon = TIME_EPSILON): boolean {
  return test >= Math.min(r0, r1) - epsilon && test <= Math.max(r0, r1) + epsilon;
}

function isEventOnLine(
  newEvent: SkeletonEvent,
  line: SkeletonLine,
  timeEpsilon = TIME_EPSILON,
  clusterEpsilon = CLUSTER_EPSILON,
): boolean {
  if (newEvent.time < line.start.time - timeEpsilon) {
    return false;
  }

  const end = popUntilValid(line);
  if (!validEvent(end)) {
    // if the end event doesn't exist or has been invalidated, then the test event is
    // valid by default
    return true;
  }

  if (line.infiniteSpeed) {
    return vec2IsBetweenUsingLengthBasedCalc(
      line.start.position[0],
      line.start.position[1],
      end.position[0],
      end.position[1],
      newEvent.position[0],
      newEvent.position[1],
      clusterEpsilon,
    );
  }
  return inRange(newEvent.time, line.start.time, end.time, timeEpsilon);
}

/**
 * Debug function
 */
export function offsetPolyLine2SSkDebug(
  pl: PolyLine2,
  offset: number,
  speeds?: EdgeSpeeds,
  cfg: SkeletonConfig = DEBUG_CONFIG,
): PolyLine2 {
  const { success, pls, errors = [] } = straightSkeletonOffset(pl, offset, speeds, cfg);
  for (const error of errors) {
    console.error(error);
  }
  if (!success) {
    return pl;
  }
  return pls[0];
}

/**
 * Offsets a PolyLine2 to a given offset distance by building a straight
 * skeleton that terminates at the offset. A scalar "speed" or "speeds" may be
 * specified to control the rate of offset for each edge. If a single
 * speed is provided, it will be used for all edges. If an array of speeds is
 * provided, the speed at index i will be used for the offset of edge i.
 *
 * Supported cases:
 * - all convex PolyLine2
 * - both closed and open PolyLine2
 * - some concave PolyLine2
 *
 * @param {PolyLine2} pl required PolyLine2 to be offset
 * @param {number} offset required offset distance
 * @param {EdgeSpeeds} speeds optional number or array of normalized scalar values; defaults to 1
 * @param {SkeletonConfig} cfg optional configuration object
 * @returns {StraightSkeletonResult} result object with polyLines and errors
 */
export function straightSkeletonOffset(
  pl: PolyLine2,
  offset: number,
  speeds?: EdgeSpeeds,
  cfg: SkeletonConfig = DEFAULT_CONFIG,
): StraightSkeletonResult {
  const { epsilon: clusterEpsilon = CLUSTER_EPSILON, repairZeroLengthEdges = false } = cfg;

  let { iterationLimit = 1000 } = cfg;

  const timeEpsilon = TIME_EPSILON;

  if (isNaN(offset)) {
    return { success: false, pls: [], errors: [SkeletonError.OFFSET_INVALID, SkeletonError.NAN_VALUE] };
  }

  if (!!pl.find((v) => isNaN(v))) {
    return { success: false, pls: [], errors: [SkeletonError.POLYLINE_INVALID, SkeletonError.NAN_VALUE] };
  }

  let sawZeroLengthEdge = false;
  for (let i = 0; i < polyLine2EdgeCount(pl); i++) {
    if (polyLine2EdgeLength(pl, i) >= CLUSTER_EPSILON) {
      continue;
    }
    sawZeroLengthEdge = true;
    break;
  }

  if (sawZeroLengthEdge) {
    if (repairZeroLengthEdges) {
      pl = polyLine2Simplify(pl);
    } else {
      return { success: false, pls: [], errors: [SkeletonError.POLYLINE_INVALID, SkeletonError.ZERO_LENGTH_EDGE] };
    }
  }

  const origWindingDir = polyLine2WindingDir(pl);
  pl = polyLine2Copy(pl);

  if (offset === 0) {
    return { success: true, pls: [pl] };
  }

  if (speeds === undefined) {
    speeds = 1;
  }

  const reversed = offset < 0;

  if (reversed) {
    if (origWindingDir === WindingDir.CW) {
      pl = polyLine2SetCW(pl);
    } else {
      pl = polyLine2SetCCW(pl);
    }
    offset = -offset;
    if (speeds instanceof Array) {
      speeds = speeds.map((s) => -s).reverse();
    } else {
      speeds = -speeds;
    }
  }

  const ctx: StraightSkeletonCtx = {
    offset,
    speeds,
    reversed,
    errors: [],
  };

  const queue = new PriorityQueue<SkeletonEvent>();

  for (const event of createStartEvents(pl, ctx)) {
    queue.insert(event, event.time);
  }

  const lines: { [key: number]: SkeletonLine } = {};

  while (queue.length > 0) {
    if (iterationLimit-- <= 0) {
      ctx.errors.push(SkeletonError.ITERATION_LIMIT_REACHED);
      break;
    }
    const batch = simultaneousBatch(queue, timeEpsilon);

    const candidates: SkeletonEvent[] = [];

    for (const cluster of clusterEvents(batch, clusterEpsilon)) {
      const combined = lineFromCluster(pl, ctx, cluster);

      if (combined === null) {
        continue;
      }

      lines[combined.id] = combined;

      // neighbors will always be at maximum 2
      for (const other of combined.neighbors) {
        const event = intersectLines(pl, combined, other, ctx, timeEpsilon, clusterEpsilon);

        if (event === null) {
          continue;
        }

        candidates.push(event);
      }
    }

    // check each candidate event to ensure it is the earliest event on each of its incoming lines
    candidate: for (const candidate of candidates) {
      for (const incoming of candidate.incoming) {
        if (!isEventOnLine(candidate, incoming, timeEpsilon, clusterEpsilon)) {
          invalidate(candidate);

          continue candidate;
        }
      }

      queue.insert(candidate, candidate.time);
    }
  }

  debugLines(lines, cfg);

  let resultPl = polyLine2FromSkeletonLines(offset, lines, polyLine2IsClosed(pl), cfg);

  if (resultPl.length === 0) {
    return { success: true, pls: [resultPl], errors: ctx.errors };
  }

  if (reversed) {
    resultPl = polyLine2Reverse(resultPl);
  }

  if (cfg?.newToOld) {
    cfg.newToOld = cfg.newToOld.reverse();
  }

  return { success: true, pls: [polyLine2SetWindingDir(resultPl, origWindingDir)], errors: ctx.errors };
}

function intersectLines(
  pl: PolyLine2,
  a: SkeletonLine,
  b: SkeletonLine,
  ctx: StraightSkeletonCtx,
  timeEpsilon = TIME_EPSILON,
  clusterEpsilon = CLUSTER_EPSILON,
): SkeletonEvent | null {
  const event = intersectionEvent(pl, ctx, a, b, clusterEpsilon);

  if (event === null) {
    return null;
  }

  const aOffset = getEdgeOffset(ctx, a.id);
  const bOffset = getEdgeOffset(ctx, b.id);

  if (ctx.reversed) {
    if (event.time >= -aOffset || event.time >= -bOffset) {
      return null;
    }
  } else {
    if (event.time >= aOffset || event.time >= bOffset) {
      return null;
    }
  }

  if (!isEventOnLine(event, a, timeEpsilon, clusterEpsilon)) {
    return null;
  }

  if (!isEventOnLine(event, b, timeEpsilon, clusterEpsilon)) {
    return null;
  }

  if (a.infiniteSpeed) {
    const dist = vec2Dist(a.start.position[0], a.start.position[1], event.position[0], event.position[1]);
    a.queue.insert(event, dist);
  } else {
    a.queue.insert(event, event.time);
  }

  if (b.infiniteSpeed) {
    const dist = vec2Dist(b.start.position[0], b.start.position[1], event.position[0], event.position[1]);
    b.queue.insert(event, dist);
  } else {
    b.queue.insert(event, event.time);
  }

  return event;
}

export function lineFromCluster(
  pl: PolyLine2,
  ctx: StraightSkeletonCtx,
  cluster: SkeletonEvent[],
  cfg?: SkeletonConfig,
): SkeletonLine | null {
  const incomingMap = cluster.reduce(
    (acc, evt) => {
      for (const incoming of evt.incoming) {
        acc[incoming.id] = incoming;
      }
      return acc;
    },
    {} as { [key: number]: SkeletonLine },
  );

  const incomingLines = Object.values(incomingMap);

  if (incomingLines.length === 0) {
    return null;
  }

  if (incomingLines.length === 1) {
    // if there is only 1 incoming event, this must be a start event: reuse the original line, but remove all
    // incoming lines from the start event
    const [incoming] = incomingLines;

    incoming.start.incoming = [];

    return incoming;
  }

  const [edges, edgesRemoved] = outermostEdges(incomingLines);
  if (edges.length < 1) {
    return null;
  }
  if (cfg?.oldToNew) {
    for (const edge of edgesRemoved) {
      cfg.oldToNew[edge] = edges;
    }
  }

  const nodeMap = incomingLines.reduce((acc, line) => {
    acc[line.id] = line;
    for (const neighbor of line.neighbors) {
      acc[neighbor.id] = neighbor;
    }
    return acc;
  }, {});

  const neighbors = outermostNodes(
    incomingLines.map((l) => ({
      id: l.id,
      neighbors: l.neighbors.map((n) => n.id),
    })),
  ).map((n) => nodeMap[n]);

  let infiniteSpeed = false;
  let [dx, dy] = speed(pl, edges, ctx);
  if (dx === 0 && dy === 0) {
    const generatingEdge = polyLine2Edge(pl, edges[0]) as unknown as Line2Segment;
    [dx, dy] = line2Vec(generatingEdge);
    infiniteSpeed = true;
  }

  const newLine = skeletonLineNew({
    start: skeletonEventNew({
      time: cluster[0].time,
      position: cluster[0].position, // all events in cluster are colocated
      incoming: incomingLines,
    }),
    dir: [dx, dy],
    neighbors,
    edges,
    infiniteSpeed,
  });

  // set all neighbors to point to the new line
  for (const neighbor of neighbors) {
    neighbor.neighbors = neighbor.neighbors.map((n) => {
      if (nodeMap[n.id] !== undefined) {
        return newLine;
      }
      return n;
    });
  }

  return newLine;
}

export function intersectionEvent(
  pl: PolyLine2,
  ctx: StraightSkeletonCtx,
  a: SkeletonLine,
  b: SkeletonLine,
  clusterEpsilon = CLUSTER_EPSILON,
): SkeletonEvent | null {
  const [asx, asy] = a.start.position;
  const [bsx, bsy] = b.start.position;
  const intersection = intersectLine2Line2(
    asx,
    asy,
    asx + a.dir[0],
    asy + a.dir[1],
    bsx,
    bsy,
    bsx + b.dir[0],
    bsy + b.dir[1],
    clusterEpsilon,
    undefined,
    false,
  );

  if (intersection instanceof CurveCurveRangeIntersection) {
    const { startx, starty, endx, endy } = intersection;
    return skeletonEventNew({
      time: (a.start.time + b.start.time) / 2,
      position: [(startx + endx) / 2, (starty + endy) / 2],
      incoming: [a, b],
    });
  }

  if (!(intersection instanceof CurveCurvePointIntersection)) {
    // TODO: this is a valid case for concave polylines but we do not yet support it
    return null;
  }

  const edgeIdx = a.edges[0];
  const governingEdge = polyLine2Edge(pl, edgeIdx) as unknown as Line2Segment;

  const speed = getEdgeOffset(ctx, edgeIdx);
  const [nx, ny] = polyLine2EdgeNormal(pl, edgeIdx);
  const [dx, dy] = vec2Scale(nx, ny, speed);

  const offsetSide = line2Side(...governingEdge, governingEdge[0] + dx, governingEdge[1] + dy);
  const intersectionSide = line2Side(...governingEdge, intersection.x, intersection.y);
  if (offsetSide !== intersectionSide) {
    return null;
  }

  const timeAtIntersection = line2PerpDistance(...governingEdge, intersection.x, intersection.y);
  return skeletonEventNew({
    time: timeAtIntersection,
    position: [intersection.x, intersection.y],
    incoming: [a, b],
  });
}

function simultaneousBatch(queue: PriorityQueue<SkeletonEvent>, epsilon = TIME_EPSILON): SkeletonEvent[] {
  const first = queue.pop();
  if (first === undefined || first === null) {
    return [];
  }
  const batch: SkeletonEvent[] = [first];
  while (queue.length > 0 && ensureDefined(queue.peek()).time - first.time < epsilon) {
    batch.push(ensureDefined(queue.pop()));
  }
  return batch;
}

function samePosition(a: Vec2, b: Vec2, epsilon = CLUSTER_EPSILON): boolean {
  return vec2Dist(a[0], a[1], b[0], b[1]) < epsilon;
}

function clusterEvents(events: SkeletonEvent[], epsilon = CLUSTER_EPSILON): SkeletonEvent[][] {
  const buckets: { position: Vec2; events: SkeletonEvent[] }[] = [];

  event: for (const event of events) {
    const [evtX, evtY] = event.position;

    // TODO: this is relatively expensive, consider using a spatial index
    for (const { position, events } of buckets) {
      if (samePosition(position, event.position, epsilon)) {
        events.push(event);
        continue event;
      }
    }

    // could not match position to existing bucket, create new one
    buckets.push({ position: [evtX, evtY], events: [event] });
  }

  return buckets.map(({ events }) => events);
}

function invalidate(event: SkeletonEvent) {
  for (const incoming of event.incoming) {
    if (popUntilValid(incoming) === event) {
      incoming.queue.pop();
    }
  }
  event.valid = false;
}

export function createStartEvents(pl: PolyLine2, ctx: StraightSkeletonCtx): SkeletonEvent[] {
  const events: { [key: number]: SkeletonEvent } = {};
  const lines: { [key: number]: SkeletonLine } = {};

  const numEdges = polyLine2EdgeCount(pl);
  const closed = polyLine2IsClosed(pl);
  const numVerts = closed ? numEdges : numEdges + 1;

  // TODO: check for zero-length edges here and skip them

  // create lines for each vertex
  for (let curr = 0; curr < numVerts; curr++) {
    const startEvent = skeletonEventNew({
      position: [...polyLine2Vertex(pl, curr)],
    });
    lastLineId = curr;
    const line = skeletonLineNew({
      id: curr,
      start: startEvent,
    });
    // this is a "hack" to be able to use `lineFromCluster` on
    // starting events as well without creating a different data
    // type for start events
    startEvent.incoming.push(line);
    lines[curr] = line;
    events[curr] = startEvent;
  }

  // hydrate lines with neighbors and edges
  for (let currVx = 0; currVx < numVerts; currVx++) {
    const event = events[currVx];
    const [line] = event.incoming;

    const isLastVx = currVx === numVerts - 1;
    const isFirstVx = currVx === 0;

    if (closed) {
      if (isFirstVx) {
        line.edges.push(numEdges - 1);
        line.neighbors.push(lines[numVerts - 1]);
      } else {
        line.edges.push(currVx - 1);
        line.neighbors.push(lines[currVx - 1]);
      }
      if (isLastVx) {
        line.neighbors.push(lines[0]);
      } else {
        line.neighbors.push(lines[currVx + 1]);
      }
      line.edges.push(currVx);
    } else {
      // polyLine is open
      if (!isLastVx) {
        line.edges.push(currVx);
        line.neighbors.push(lines[currVx + 1]);
      }
      if (!isFirstVx) {
        line.edges.push(currVx - 1);
        line.neighbors.push(lines[currVx - 1]);
      }
    }

    line.dir = speed(pl, line.edges, ctx);
  }

  return Object.values(events);
}

// TODO: cache the results of this function
function speed(pl: PolyLine2, edges: number[], ctx: StraightSkeletonCtx): Vec2 {
  let vx = 0;
  let vy = 0;
  if (edges.length === 1) {
    [vx, vy] = polyLine2EdgeNormal(pl, edges[0]);
    [vx, vy] = vec2Scale(vx, vy, getEdgeOffset(ctx, edges[0]));
  } else {
    const [a, b] = edges;
    const atZero = edgeEdgeIntersection(pl, NO_OP_CTX, a, b);
    if (atZero instanceof CurveCurvePointIntersection) {
      const atSpeed = edgeEdgeIntersection(pl, ctx, a, b);
      if (atSpeed instanceof CurveCurvePointIntersection) {
        [vx, vy] = [atSpeed.x - atZero.x, atSpeed.y - atZero.y];
      } else {
        [vx, vy] = polyLine2EdgeNormal(pl, a);
        [vx, vy] = vec2Scale(vx, vy, getEdgeOffset(ctx, a));
      }
    }
  }
  if (isNaN(vx) || isNaN(vy)) {
    ctx.errors.push(SkeletonError.NAN_VALUE);
    return [1, 1];
  }
  return [vx, vy];
}

// TODO: cache the results of this function
function offsetEdge(pl: PolyLine2, ctx: StraightSkeletonCtx, index: number): Line2Segment {
  const [nx, ny] = polyLine2EdgeNormal(pl, index);
  const [dx, dy] = vec2Scale(nx, ny, getEdgeOffset(ctx, index));
  return polyLine2Move(polyLine2Edge(pl, index), dx, dy) as unknown as Line2Segment;
}

// TODO: cache the results of this function
function edgeEdgeIntersection(
  pl: PolyLine2,
  ctx: StraightSkeletonCtx,
  a: number,
  b: number,
): CurveCurveIntersection | null {
  const prevEdgeSegment = offsetEdge(pl, ctx, a);
  const nextEdgeSegment = offsetEdge(pl, ctx, b);

  return intersectLine2Line2(...prevEdgeSegment, ...nextEdgeSegment, undefined, undefined, false);
}

function getEdgeOffset(ctx: StraightSkeletonCtx, edge: number): number {
  return ctx.offset * getEdgeSpeed(ctx.speeds, edge);
}

function getEdgeSpeed(speeds: EdgeSpeeds, edge: number): number {
  if (typeof speeds === "number") {
    return speeds;
  }
  return speeds[edge];
}

export interface NodeWithNeighbors {
  id: number;
  neighbors: number[];
}

export function outermostNodes(nodes: NodeWithNeighbors[]): number[] {
  const hash: { [key: number]: number } = {};

  // XOR neighbors (this convolution will leave 2 outermost neighbors)
  for (const neighbor of nodes.map((l) => l.neighbors).flat()) {
    if (hash[neighbor] !== undefined) {
      delete hash[neighbor];
    } else {
      hash[neighbor] = neighbor;
    }
  }

  // cleanup pass to remove inner of the 2 outermost neighbors
  for (const line of nodes) {
    if (hash[line.id] !== undefined) {
      delete hash[line.id];
    }
  }

  return Object.values(hash);
}

export interface NodeEdges {
  edges: number[];
}

export function outermostEdges(nodes: NodeEdges[]): [number[], number[]] {
  const hash: { [key: number]: boolean } = {};

  const removed: { [key: number]: boolean } = {};

  // XOR edges
  for (const idx of nodes.map((l) => l.edges).flat()) {
    if (hash[idx] !== undefined) {
      delete hash[idx];
    } else {
      hash[idx] = true;
      removed[idx] = true;
    }
  }

  const edges = Object.keys(hash).map((k) => {
    delete removed[k];
    return parseInt(k);
  });
  const edgesRemoved = Object.keys(removed).map((k) => parseInt(k));

  return [edges, edgesRemoved];
}

function terminatedLines(allLines: { [key: number]: SkeletonLine }): SkeletonLine[] {
  return Object.values(allLines).filter((line) => popUntilValid(line) !== null);
}

function unterminatedLines(allLines: { [key: number]: SkeletonLine }): SkeletonLine[] {
  return Object.values(allLines).filter((line) => validEvent(line.start) && popUntilValid(line) === null);
}

function polyLine2FromSkeletonLines(
  timeLimit: number,
  allLines: { [key: number]: SkeletonLine },
  polyLineIsClosed: boolean,
  cfg?: SkeletonConfig,
): PolyLine2 {
  const unterminated: SkeletonLine[] = unterminatedLines(allLines);

  if (unterminated.length === 0) {
    return polyLine2New([]);
  }

  const terminated = terminatedLines(allLines).reduce((acc, line) => {
    acc[line.id] = true;
    return acc;
  }, {});

  // walk in one direction to find the first event
  const visited = new Set<number>();
  let previous: SkeletonLine | null = null;
  let current = unterminated[0];
  const inOrder: SkeletonLine[] = [];
  while (!visited.has(current.id)) {
    visited.add(current.id);
    inOrder.push(current);

    let nextNeighbor: SkeletonLine | null = null;
    for (const neighbor of current.neighbors) {
      if (neighbor === previous) {
        continue;
      }
      nextNeighbor = neighbor;
      break;
    }
    if (nextNeighbor === null) {
      break;
    }
    previous = current;
    current = nextNeighbor;

    if (terminated[current.id]) {
      break;
    }
  }

  // walk forwards from the first event
  previous = inOrder[1] ?? null;
  current = unterminated[0];

  inOrder.reverse();

  while (true) {
    let nextNeighbor: SkeletonLine | null = null;
    for (const neighbor of current.neighbors) {
      if (terminated[neighbor.id]) {
        continue;
      }
      if (neighbor === previous) {
        continue;
      }
      nextNeighbor = neighbor;
      break;
    }
    if (nextNeighbor === null) {
      break;
    }

    previous = current;
    current = nextNeighbor;
    inOrder.push(current);
    if (visited.has(current.id)) {
      break;
    }
    visited.add(current.id);
  }

  const points: number[] = [];
  let curr: SkeletonLine;
  let next: SkeletonLine;
  let prev: number = -1;
  for (let i = 0; i < inOrder.length; i++) {
    curr = inOrder[i];

    // wrap to first line if we are at the end and the polyLine is closed
    const shouldWrap = polyLineIsClosed && i === inOrder.length - 1;
    next = inOrder[shouldWrap ? 0 : i + 1];

    const timeRemaining = timeLimit - curr.start.time;
    const [px, py] = curr.start.position;
    const [nx, ny] = curr.dir;
    const [sx, sy] = [(nx * timeRemaining) / timeLimit, (ny * timeRemaining) / timeLimit];
    const [ex, ey] = [px + sx, py + sy];

    if (cfg?.newToOld) {
      const edge = (!!next ? arrayIntersect(curr.edges, next.edges) : curr.edges)[0];
      if (edge !== prev) {
        cfg.newToOld.push(edge);
      }
      prev = edge;
    }

    points.push(ex, ey);
  }

  return polyLine2New(points);
}

function arrayIntersect(a: number[], b: number[]): number[] {
  const hash: { [key: number]: boolean } = {};
  for (const v of a) {
    hash[v] = true;
  }
  for (const v of b) {
    if (hash[v]) {
      hash[v] = false;
    } else {
      hash[v] = true;
    }
  }
  return Object.keys(hash)
    .filter((k) => !hash[k])
    .map((k) => parseInt(k));
}

function debugLines(allLines: { [key: number]: SkeletonLine }, cfg: SkeletonConfig) {
  if (!cfg?.debug?.lines) {
    return;
  }

  const unaccounted = Object.keys(allLines).reduce((acc, key) => {
    acc[key] = true;
    return acc;
  }, {});
  for (const line of terminatedLines(allLines)) {
    const last = popUntilValid(line);
    if (!last) {
      continue;
    }
    delete unaccounted[line.id];
    cfg.debug.lines.push({
      pline: polyLine2New([line.start.position[0], line.start.position[1], last.position[0], last.position[1]]),
      mat: 0xffdddd,
    });
    if (cfg.debug.text) {
      const last = line.queue.peek();
      if (last) {
        const x1 = line.start.position[0];
        const y1 = line.start.position[1];
        const x2 = last.position[0];
        const y2 = last.position[1];
        cfg.debug.text.push({
          text: `S${line.id}`,
          scale: 10,
          x: (x1 + x2) / 2,
          y: (y1 + y2) / 2,
        });
        if (cfg.debug.points) {
          cfg.debug.points.push({
            x: x1,
            y: y1,
            shape: "square",
          });
          cfg.debug.points.push({
            x: x2,
            y: y2,
            shape: "x",
          });
        }
      }
    }
  }
  for (const line of unterminatedLines(allLines)) {
    delete unaccounted[line.id];
    const x1 = line.start.position[0];
    const y1 = line.start.position[1];
    const x2 = x1 + line.dir[0];
    const y2 = y1 + line.dir[1];
    cfg.debug.lines.push({
      pline: polyLine2New([x1, y1, x2, y2]),
      mat: 0xffdddd,
    });
    if (cfg.debug.text) {
      cfg.debug.text.push({
        text: `S${line.id}`,
        scale: 2,
        x: (x1 + x2) / 2,
        y: (y1 + y2) / 2,
      });
    }
    if (cfg.debug.points) {
      cfg.debug.points.push({
        x: x1,
        y: y1,
        shape: "square",
      });
      cfg.debug.points.push({
        x: x2,
        y: y2,
        shape: "x",
      });
    }
  }
  for (const id of Object.keys(unaccounted)) {
    const line = allLines[parseInt(id)];
    cfg.debug.lines.push({
      pline: polyLine2New([
        line.start.position[0],
        line.start.position[1],
        line.start.position[0] + line.dir[0],
        line.start.position[1] + line.dir[1],
      ]),
      mat: 0x00dd00,
    });
  }
}
